import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { STATUTS, etoiles, messageErreur } from "./libelles";
import { ErreurBase, appeler } from "./supabase";
import type { LigneRecherche, Statut } from "./types";

export type EtatRecherche = { readonly texte: string; readonly statut: Statut | "" };

/**
 * Chercher un mot. Sans texte : ce dont les joueurs se plaignent, le plus
 * signalé et le plus mal noté d'abord — ce qu'il faut relire en premier.
 */
export function Recherche(props: {
  readonly etat: EtatRecherche;
  readonly onChange: (etat: EtatRecherche) => void;
  readonly onOuvrir: (id: string) => void;
}) {
  const [saisie, setSaisie] = useState(props.etat.texte);
  const [lignes, setLignes] = useState<readonly LigneRecherche[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let actuel = true;
    setLignes(null);
    setErreur(null);
    appeler<LigneRecherche[]>("admin_chercher", {
      p_texte: props.etat.texte === "" ? null : props.etat.texte,
      p_statut: props.etat.statut === "" ? null : props.etat.statut,
    })
      .then((resultat) => actuel && setLignes(resultat))
      .catch((e: unknown) => actuel && setErreur(messageErreur(e instanceof ErreurBase ? e.code : "erreur_inconnue")));
    return () => {
      actuel = false;
    };
  }, [props.etat]);

  const chercher = (evenement: FormEvent) => {
    evenement.preventDefault();
    props.onChange({ ...props.etat, texte: saisie.trim() });
  };

  const titre =
    props.etat.texte !== ""
      ? `Résultats pour « ${props.etat.texte} »`
      : props.etat.statut !== ""
        ? `Entrées « ${STATUTS[props.etat.statut]} »`
        : "À relire en priorité";

  return (
    <section>
      <form role="search" onSubmit={chercher} className="ligne-outils">
        <label className="grandit">
          Mot, identifiant ou morceau de définition
          <input type="search" value={saisie} onChange={(e) => setSaisie(e.target.value)} placeholder="falaise, fr-0412…" />
        </label>
        <label>
          Statut
          <select
            value={props.etat.statut}
            onChange={(e) => props.onChange({ ...props.etat, statut: e.target.value as Statut | "" })}
          >
            <option value="">Tous</option>
            {(Object.keys(STATUTS) as Statut[]).map((statut) => (
              <option key={statut} value={statut}>
                {STATUTS[statut]}
              </option>
            ))}
          </select>
        </label>
        <button type="submit" className="principal">
          Chercher
        </button>
      </form>

      <h1>{titre}</h1>
      {props.etat.texte === "" && props.etat.statut === "" && (
        <p className="discret">Les mots notés ou signalés par les joueurs, les plus signalés puis les plus mal notés d'abord.</p>
      )}
      {erreur !== null && (
        <p role="alert" className="erreur">
          {erreur}
        </p>
      )}
      {lignes === null && erreur === null && <p role="status">Recherche…</p>}
      {lignes !== null && lignes.length === 0 && <p role="status">Aucune entrée.</p>}
      {lignes !== null && lignes.length > 0 && (
        <ul className="resultats" aria-label="Résultats">
          {lignes.map((ligne) => (
            <li key={ligne.id}>
              <button type="button" className="resultat" onClick={() => props.onOuvrir(ligne.id)}>
                <span className="resultat-tete">
                  <strong className="mot">{ligne.mot}</strong>
                  <span className={`pastille ${ligne.statut}`}>{STATUTS[ligne.statut]}</span>
                  <span className="discret">{ligne.id}</span>
                  {ligne.reservee && <span className="pastille reservee">Réservé</span>}
                </span>
                <span className="resultat-def">{ligne.definition}</span>
                {(ligne.notes > 0 || ligne.signalements > 0) && (
                  <span className="discret">
                    {ligne.notes > 0 && `${etoiles(ligne.moyenne)} (${ligne.notes} note${ligne.notes > 1 ? "s" : ""})`}
                    {ligne.notes > 0 && ligne.signalements > 0 && " · "}
                    {ligne.signalements > 0 && `${ligne.signalements} signalement${ligne.signalements > 1 ? "s" : ""}`}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
